import { useCallback } from 'react';

import { SipDeviceRowHookReturnType } from './useSipDeviceRow.decl';

import { SUCCESS_FEEDBACK_MESSAGES } from '@constants/translation.constants';
import {
  DeleteSipSettingMutation,
  DeleteSipSettingMutationVariables,
} from '@generated/DeleteSipSettingMutation';
import {
  ToggleSipSettingMutation,
  ToggleSipSettingMutationVariables,
} from '@generated/ToggleSipSettingMutation';
import { UserFullFragment_sipSettings } from '@generated/UserFullFragment';
import { DELETE_SIP_SETTING_MUTATION } from '@graphql/mutations/DeleteSipSettingMutation';
import { TOGGLE_SIP_SETTING_MUTATION } from '@graphql/mutations/ToggleSipSettingMutation';
import { getTrackSettingsProperties } from '@helpers/tracker.helpers';
import { useGlobalData } from '@hooks/useGlobalData/useGlobalData';
import { useGraphMutationWithFeedback } from '@hooks/useMutation';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/**
 * Hook to update and / or delete a sip device given a sip setting id.
 * @param sipSettingID - SIP setting ID
 * @returns an object holding a function responsible for updating, a function responsible for deleting a sip device and its state
 */
export function useSipDeviceRow(sipSettingID: string): SipDeviceRowHookReturnType {
  const { t } = useTranslation();
  const { currentUser } = useGlobalData();
  const { userId } = useParams();

  const [toggleSipSetting, { loading: isToggleLoading }] = useGraphMutationWithFeedback<
    ToggleSipSettingMutation,
    ToggleSipSettingMutationVariables
  >(TOGGLE_SIP_SETTING_MUTATION, { type: 'snackbar' });

  const [deleteSipSetting] = useGraphMutationWithFeedback<
    DeleteSipSettingMutation,
    DeleteSipSettingMutationVariables
  >(DELETE_SIP_SETTING_MUTATION, { type: 'snackbar' });

  const isOwnUser = currentUser.ID === userId;

  const handleDeleteSipDevice = useCallback(async () => {
    await deleteSipSetting(
      {
        variables: {
          input: {
            agentID: userId,
            ID: sipSettingID,
          },
        },
        update(cache, { data }) {
          cache.modify({
            id: cache.identify({ __typename: 'AgentV2', ID: userId }),
            fields: {
              sipSettings: (sipSettings) =>
                sipSettings.filter(
                  (sipSetting: UserFullFragment_sipSettings) =>
                    sipSetting.ID !== data!.deleteSipSetting.ID
                ),
            },
          });
        },
        optimisticResponse: {
          deleteSipSetting: {
            __typename: 'DeleteSipSettingOutput',
            ID: sipSettingID,
          },
        },
      },
      {
        successMessage: t('success_feedback_messages.deletion_success', {
          name: t('user_profile.call_preferences.forward_call_sip.sip_device'),
        }),
      }
    );
  }, [deleteSipSetting, sipSettingID, userId, t]);

  const handleToggleChange = useCallback(
    async (isEnabled: boolean) => {
      const action = isEnabled ? 'enabled' : 'disabled';

      await toggleSipSetting(
        {
          variables: {
            input: {
              agentID: userId,
              ID: sipSettingID,
              isEnabled,
            },
          },
          update(cache, { data }) {
            cache.modify({
              id: cache.identify({ __typename: 'AgentV2', ID: userId }),
              fields: {
                sipSettings: (sipSettings) =>
                  sipSettings.map((sipSetting: UserFullFragment_sipSettings) =>
                    sipSetting.ID === data!.toggleSipSetting.ID
                      ? { ...sipSetting, isEnabled }
                      : sipSetting
                  ),
              },
            });
          },
          optimisticResponse: {
            toggleSipSetting: {
              __typename: 'AgentSipSetting',
              ID: sipSettingID,
              isEnabled,
            },
          },
        },
        {
          successMessage: t(SUCCESS_FEEDBACK_MESSAGES.UPDATE_SUCCESS, {
            name: t('user_profile.call_preferences.forward_call_sip.sip_configuration'),
          }),
          trackerProperties: getTrackSettingsProperties({
            event: 'user_settings_edited',
            settingsSection: 'Forward calls to SIP device',
            setting: 'SIP devices',
            field: `sip_device_${sipSettingID}`,
            action,
            isOwnUser,
          }),
        }
      );
    },
    [toggleSipSetting, sipSettingID, userId, t, isOwnUser]
  );

  return {
    isToggleLoading,
    handleDeleteSipDevice,
    handleToggleChange,
  };
}
